import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import SafeIcon from "../common/SafeIcon";
import AudioPlayer from "./AudioPlayer";
import SummaryList from "./SummaryList";
import MeetingDetailsForm from "./MeetingDetailsForm";
import ReferenceModal from "./ReferenceModal"; // Component ที่เพิ่งสร้าง
import AttachmentModal from "./AttachmentModal"; // Component ที่เพิ่งสร้าง
// ที่ด้านบนสุดของไฟล์ TranscriptPanel.jsx

import { format, isBefore } from "date-fns";
import { th } from "date-fns/locale";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
const {
  FiCopy,
  FiDownload,
  FiSearch,
  FiX,
  FiPlus,
  FiSave,
  FiShare2,
  FiCheck,
  FiChevronUp,
  FiChevronDown,
  FiList,
  FiInfo,
  FiShield,
  FiTag,
  FiRefreshCw,
  FiLink2,
  FiExternalLink,
  FiClock,
  FiDatabase,
  FiLayers,
  FiPaperclip,
  FiDownloadCloud,
  FiLogOut,
  FiUpload,
  FiEdit3,
  FiBookmark,
  FiToggleLeft,
  FiToggleRight,
} = FiIcons;
const SortableReferenceTag = ({ id, title, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 flex items-center gap-1.5 bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-green-500"
      >
        ::
      </span>
      <span>{title}</span>
      <button
        onClick={() => onRemove(id)}
        className="ml-1 text-green-600 hover:text-green-800"
      >
        <FiX size={14} />
      </button>
    </div>
  );
};

const AttachmentTag = ({ attachment, onRemove, onToggleUse }) => {
  const now = new Date();
  const isExpired = isBefore(new Date(attachment.expiryDate), now);

  let tagClasses = "bg-slate-100 text-slate-800";
  let titleText = `ไม่ได้ใช้ในแชท | ${attachment.fileName}`;
  let ToggleIcon = FiToggleLeft;
  let toggleTitle = "คลิกเพื่อใช้ในแชท";

  if (isExpired) {
    tagClasses = "bg-red-100 text-red-800";
    titleText = `หมดอายุแล้ว | ${attachment.fileName}`;
  } else if (attachment.useInChat) {
    tagClasses = "bg-green-100 text-green-800";
    titleText = `ใช้ในแชท | ${attachment.fileName}`;
    ToggleIcon = FiToggleRight;
    toggleTitle = "คลิกเพื่อหยุดใช้ในแชท";
  }

  return (
    <div
      className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${tagClasses}`}
      title={titleText}
    >
      <span className="truncate">{attachment.fileName}</span>
      {!isExpired && (
        <button
          onClick={onToggleUse}
          className="opacity-60 hover:opacity-100"
          title={toggleTitle}
        >
          <ToggleIcon size={16} />
        </button>
      )}
      <button
        onClick={onRemove}
        className="ml-1 opacity-60 hover:opacity-100"
        title="นำออก"
      >
        <FiX size={14} />
      </button>
    </div>
  );
};
const CollapsibleSection = ({
  icon,
  title,
  count,
  onAdd,
  children,
  startOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(startOpen);
  return (
    <div className="p-3 bg-white border-t border-slate-200">
      <div
        className="flex items-center justify-between cursor-pointer group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <SafeIcon icon={icon} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">
            {title} ({count})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAdd();
            }}
            className="text-xs font-medium text-green-600 hover:text-green-800 hover:underline"
          >
            + {title === "รายการอ้างอิง" ? "เพิ่ม/แก้ไข" : "จัดการไฟล์"}
          </button>
          <motion.div
            animate={{ rotate: isOpen ? 0 : -90 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronDown className="text-slate-400 group-hover:text-slate-600" />
          </motion.div>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto", marginTop: "0.5rem" },
              collapsed: { opacity: 0, height: 0, marginTop: "0rem" },
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
const TranscriptPanel = ({
  jobId,
  activeMeetingId,
  transcript,
  fileName,
  metadata,
  onReset,
  onSave,
  meetingDetails,
  onUpdateDetails,
  summaries,
  onDeleteSummary,
  onAIAssist,
  onSendEmail,
  allMeetings,
  referencedMeetingIds,
  onUpdateReferences,
  attachments,
  onUpdateAttachments,
  onUpdateSummaries,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedText, setHighlightedText] = useState("");
  const [isMarkdown, setIsMarkdown] = useState(true);
  const [searchMatches, setSearchMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [isCopied, setIsCopied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showSummaryList, setShowSummaryList] = useState(false);
  const [isDetailsFormOpen, setIsDetailsFormOpen] = useState(false);
  const transcriptRef = useRef(null);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));
  const safeReferencedIds = referencedMeetingIds || [];
  const safeAttachments = attachments || [];
  const [showSaveReminder, setShowSaveReminder] = useState(false);
  const saveReminderTimerRef = useRef(null);
  const handleReferenceDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = safeReferencedIds.indexOf(active.id);
      const newIndex = safeReferencedIds.indexOf(over.id);
      onUpdateReferences(arrayMove(safeReferencedIds, oldIndex, newIndex));
    }
  };
  const startSaveReminderTimer = () => {
    // 1. เคลียร์ Timer เก่าเสมอ
    if (saveReminderTimerRef.current) {
      clearInterval(saveReminderTimerRef.current);
    }
    // 2. ซ่อน Tooltip
    setShowSaveReminder(false);

    // 3. ตั้ง Timer ใหม่ให้ทำงานในอีก 5 นาที
    saveReminderTimerRef.current = setInterval(() => {
      setShowSaveReminder(true);
      // **สำคัญ:** เมื่อ Tooltip แสดงแล้ว ให้เคลียร์ Timer นี้ทิ้งไปเลย
      // เพื่อป้องกันไม่ให้มันทำงานซ้ำอีกโดยไม่จำเป็น
      if (saveReminderTimerRef.current) {
        clearInterval(saveReminderTimerRef.current);
        saveReminderTimerRef.current = null;
      }
    }, 5 * 60 * 1000); // 5 นาที
  };
  const handleDismissReminder = (e) => {
    e.stopPropagation();
    setShowSaveReminder(false);
    // **สำคัญ:** เมื่อผู้ใช้กดปิดเอง ให้เริ่มนับเวลาใหม่
    startSaveReminderTimer();
  };
  const handleRemoveReference = (idToRemove) => {
    onUpdateReferences(safeReferencedIds.filter((id) => id !== idToRemove));
  };
  const handleRemoveAttachment = (idToRemove) => {
    const updatedAttachments = safeAttachments.filter(
      (att) => att.id !== idToRemove
    );
    onUpdateAttachments(updatedAttachments);
  };

  const handleToggleAttachmentUse = (idToToggle) => {
    const updatedAttachments = safeAttachments.map((att) =>
      att.id === idToToggle ? { ...att, useInChat: !att.useInChat } : att
    );
    onUpdateAttachments(updatedAttachments);
  };
  const meetingsMap = new Map((allMeetings || []).map((m) => [m.id, m]));
  // --- START: ฟังก์ชันและ Hooks ทั้งหมด ---
  useEffect(() => {
    // Reset all internal state when a new meeting is loaded
    setSearchTerm("");
    setHighlightedText(transcript);
    setSearchMatches([]);
    setCurrentMatchIndex(-1);
    setIsCopied(false);
    setIsSaved(false);
    setShowSummaryList(false);
    setIsDetailsFormOpen(false);

    startSaveReminderTimer();
    return () => {
      if (saveReminderTimerRef.current) {
        clearInterval(saveReminderTimerRef.current);
      }
    };
  }, [transcript, fileName, jobId]); // Dependency array

  useEffect(() => {
    if (searchTerm && transcript) {
      const regex = new RegExp(searchTerm, "gi");
      const matches = [...transcript.matchAll(regex)];
      setSearchMatches(matches);
      setCurrentMatchIndex(matches.length > 0 ? 0 : -1);
      const highlighted = transcript.replace(
        regex,
        (match) =>
          `<mark class="bg-yellow-300 px-1 py-0.5 rounded">${match}</mark>`
      );
      setHighlightedText(highlighted);
    } else {
      setHighlightedText(transcript);
      setSearchMatches([]);
      setCurrentMatchIndex(-1);
    }
  }, [searchTerm, transcript]);

  useEffect(() => {
    if (currentMatchIndex >= 0 && searchMatches.length > 0) {
      setTimeout(() => {
        const element = transcriptRef.current;
        if (element) {
          const marks = element.getElementsByTagName("mark");
          if (marks[currentMatchIndex]) {
            marks[currentMatchIndex].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            marks.forEach((mark, index) => {
              if (index === currentMatchIndex) {
                mark.classList.add(
                  "bg-yellow-400",
                  "ring-2",
                  "ring-yellow-500"
                );
                mark.classList.remove("bg-yellow-300");
              } else {
                mark.classList.add("bg-yellow-300");
                mark.classList.remove(
                  "bg-yellow-400",
                  "ring-2",
                  "ring-yellow-500"
                );
              }
            });
          }
        }
      }, 100);
    }
  }, [currentMatchIndex, searchMatches]);

  const handleNextMatch = () => {
    if (searchMatches.length > 0) {
      setCurrentMatchIndex((prev) => (prev + 1) % searchMatches.length);
    }
  };

  const handlePrevMatch = () => {
    if (searchMatches.length > 0) {
      setCurrentMatchIndex(
        (prev) => (prev - 1 + searchMatches.length) % searchMatches.length
      );
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleUpdateSummaryContent = (summaryId, newContent) => {
    const updatedSummaries = summaries.map((summary) => {
      if (summary.id === summaryId) {
        return { ...summary, content: newContent };
      }
      return summary;
    });
    onUpdateSummaries(updatedSummaries);
  };

  const handleRetranscribeClick = () => {
    // Show an alert for the future feature
    confirm(
      "สามารถถอดข้อความอีกรอบได้หากยังไม่หมดเวลาเก็บไฟล์\n\n---พร้อมใช้งานในเวอร์ชันถัดไป---\n\nหากต้องการถอดข้อความใหม่ กรุณาลบและอัพโหลดใหม่"
    );
  };
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: fileName || "Transcript",
          text: transcript,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      handleCopy();
    }
  };

  // --- START: ฟังก์ชัน Export ที่แสดง "หัวข้อการประชุม" ด้วย ---
  const handleExport = () => {
    // 1. ค้นหาข้อมูลการประชุม
    const meetingToExport = allMeetings.find((m) => m.id === activeMeetingId);

    if (!meetingToExport) {
      alert("ไม่พบข้อมูลการประชุมที่จะ Export");
      return;
    }

    // 2. ดึงข้อมูลสำหรับแสดงผล: ชื่อไฟล์ และ "หัวข้อการประชุม"
    const fileTitle = meetingToExport.title || "Untitled Meeting";
    const detailsTitle = meetingToExport.meetingDetails?.title;

    // 3. สร้างบรรทัด "หัวข้อ" แบบมีเงื่อนไข
    //    (จะแสดงก็ต่อเมื่อมี detailsTitle, ไม่ใช่ค่าว่าง, และไม่ซ้ำกับ fileTitle)
    let detailsTitleLine = "";
    if (
      detailsTitle &&
      detailsTitle.trim() !== "" &&
      detailsTitle !== fileTitle
    ) {
      detailsTitleLine = `\nหัวข้อ: "${detailsTitle}"`;
    }

    // 4. Format วันที่
    const lastModifiedDate = format(
      new Date(meetingToExport.lastModified || meetingToExport.createdAt),
      "d MMM yyyy, HH:mm",
      { locale: th }
    );

    // 5. ประกอบร่างข้อความยืนยันทั้งหมด
    const confirmationMessage = `คุณกำลังจะ Export ข้อมูลการประชุม:

ไฟล์: "${fileTitle}"${detailsTitleLine}
แก้ไขล่าสุด: ${lastModifiedDate}

ข้อมูลที่ Export จะรวมถึงการถอดความ, ประวัติการแชท, และสรุปทั้งหมด ต้องการดำเนินการต่อหรือไม่?`;

    // 6. แสดง Dialog และตรวจสอบการยืนยัน
    const userConfirmed = window.confirm(confirmationMessage);

    if (!userConfirmed) {
      return;
    }

    // --- ถ้าผู้ใช้ยืนยัน ให้ดำเนินการ Export ต่อไป ---

    const exportPayload = {
      appSignature: "YungYuenYor_Export_v1",
      exportedAt: new Date().toISOString(),
      data: meetingToExport,
    };
    const jsonString = JSON.stringify(exportPayload, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    // ใช้ fileTitle เพื่อความสอดคล้อง
    const safeFileName = fileTitle
      .replace(/[^a-zA-Z0-9\u0E00-\u0E7F\s._-]/g, "")
      .replace(/\s+/g, "_");
    a.href = url;
    a.download = `${safeFileName || "meeting_export"}.yyy.json`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  // --- END: ฟังก์ชัน Export ---

  const handleDownload = () => {
    const blob = new Blob([transcript], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName || "transcript"}.${isMarkdown ? "md" : "txt"}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleMarkdown = () => {
    setIsMarkdown(!isMarkdown);
    if (!isMarkdown) {
      setSearchTerm("");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchMatches([]);
    setCurrentMatchIndex(-1);
  };

  const toggleSummaryList = () => {
    setShowSummaryList(!showSummaryList);
  };

  const handleSaveMeetingDetails = (details) => {
    onUpdateDetails(details);
  };

  const handleSave = () => {
    const success = onSave();
    if (success) {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);

      startSaveReminderTimer();
    }
  };
  const handleUpdateSummaryTitle = (summaryId, newTitle) => {
    const updatedSummaries = summaries.map((summary) => {
      if (summary.id === summaryId) {
        return { ...summary, title: newTitle.trim() };
      }
      return summary;
    });
    onUpdateSummaries(updatedSummaries); // <-- **เรียกใช้ฟังก์ชันที่ถูกต้อง**
  };

  const handleNewClick = () => {
    if (transcript) {
      const userConfirmed = window.confirm(
        "คุณมีข้อมูลที่ยังไม่ได้บันทึก หากดำเนินการต่อ ข้อมูลปัจจุบันจะถูกลบ\n\nDo you want to discard your current work and start a new session?"
      );
      if (userConfirmed) {
        onReset();
      }
    } else {
      onReset();
    }
  };
  const handleCopyJobId = async () => {
    if (jobId) {
      await navigator.clipboard.writeText(jobId);
      alert(`jobId ${jobId} copied to clipboard!`);
    }
  };
  const markdownComponents = {
    mark: ({ children, ...props }) => (
      <mark className="bg-yellow-300 px-1 py-0.5 rounded" {...props}>
        {children}
      </mark>
    ),
  };

  // --- END: ฟังก์ชันและ Hooks ทั้งหมด ---

  if (!transcript) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        <div className="text-center">
          <SafeIcon
            icon={FiSearch}
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
          />
          <p className="text-lg mb-2">ไม่มีการถอดความที่พร้อมใช้งาน</p>
          <p className="text-sm">อัปโหลดไฟล์เสียงหรือวิดีโอเพื่อเริ่มต้น</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="h-full flex flex-col bg-white relative"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* ส่วน Header ของ Panel */}
      {/* ส่วน Header ของ Panel */}
      <div className="p-4 border-b border-gray-100 border-r border-gray-200 flex-shrink-0">
        {/* --- START: แถวบนสุด (Header) ที่แก้ไขแล้ว --- */}
        <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-4">
          <h2 className="text-lg font-semibold text-gray-800">การถอดข้อความ</h2>

          <div className="flex items-center flex-wrap justify-end gap-x-1.5">
            {/* Session Actions */}
            <div className="relative flex items-center p-0.5 bg-slate-100 rounded-lg border">
              <button
                onClick={handleNewClick}
                className="p-1 rounded-md hover:bg-gray-200"
                title="New"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={handleSave}
                className={`p-1 rounded-md ${
                  isSaved ? "bg-green-100" : "hover:bg-gray-200"
                }`}
                title="Save"
              >
                <SafeIcon
                  icon={isSaved ? FiCheck : FiSave}
                  className={`w-4 h-4 ${
                    isSaved ? "text-green-600" : "text-gray-600"
                  }`}
                />
              </button>
              <AnimatePresence>
                {showSaveReminder && (
                  <motion.div
                    initial={{ opacity: 0, y: -5, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -5, scale: 0.95 }}
                    // --- 1. เปลี่ยนตำแหน่งเป็น "top-full" ---
                    className="absolute top-full right-0 mt-2 z-50 w-max p-1.5 pl-2.5 bg-green-100 border border-green-300 text-green-800 text-xs font-semibold rounded-md shadow-lg flex items-center gap-1.5"
                  >
                    <span>อย่าลืมกดบันทึกนะ!</span>
                    <button
                      onClick={handleDismissReminder}
                      className="p-0.5 hover:bg-green-200 rounded-full"
                    >
                      <FiX size={14} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* View & Details Actions */}
            <div className="flex items-center p-0.5 bg-gray-100 rounded-md border relative group">
              <button
                onClick={() => setIsDetailsFormOpen(true)}
                className="p-1 rounded-md hover:bg-gray-200"
                title="แก้ไขรายละเอียดการประชุม"
              >
                <SafeIcon icon={FiEdit3} className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={toggleSummaryList}
                className={`p-1 rounded-md ${
                  showSummaryList ? "bg-green-100" : "hover:bg-gray-200"
                }`}
                title="ดูรายการที่บันทึก"
              >
                <SafeIcon
                  icon={FiBookmark}
                  className={`w-4 h-4 ${
                    showSummaryList ? "text-green-600" : "text-gray-600"
                  }`}
                />
              </button>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 z-10">
                รายละเอียด & สรุป
              </div>
            </div>
            <div className="flex items-center p-0.5 bg-gray-100 rounded-md border relative group">
              <button
                onClick={handleExport}
                className="p-1 rounded-md hover:bg-slate-200"
                title="Export Meeting Data"
              >
                <SafeIcon icon={FiUpload} className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
        {/* --- END: แถวบนสุด --- */}

        {/* --- START: แถวข้อมูลไฟล์ที่ออกแบบใหม่ให้เป็น Responsive --- */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          {/* --- 1. ชื่อไฟล์ --- */}
          {/* ใช้ flex-shrink เพื่อให้ยอมหดตัว และ min-w-[200px] เพื่อไม่ให้หดเล็กเกินไป */}
          <p
            className="text-lg font-semibold text-slate-800 truncate flex-shrink min-w-[200px]"
            title={fileName}
          >
            {fileName || "Untitled"}
          </p>

          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 flex-shrink-0">
            {metadata?.duration && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <FiClock size={12} />
                <span className="font-medium text-slate-700">
                  {metadata.duration}
                </span>
              </div>
            )}
            {metadata?.fileSize && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <FiDatabase size={12} />
                <span className="font-medium text-slate-700">
                  {metadata.fileSize}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* --- END: แถวข้อมูลไฟล์ --- */}

        {!showSummaryList && (
          <div className="relative mt-4 flex items-center gap-2">
            {/* --- Input Field (flex-1 ทำให้ยืดหยุ่น) --- */}
            <div className="relative flex-1 min-w-0">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none"
              />
              <input
                type="text"
                placeholder="ปลด Markdown ก่อนทำการค้นหา..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isMarkdown}
                className="w-full pl-10 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                title={isMarkdown ? "กรุณาปิด Markdown (MD) ก่อนค้นหา" : ""}
              />
            </div>

            {/* --- Search Navigation (แสดงเมื่อมีการค้นหา) --- */}
            <AnimatePresence>
              {searchTerm && !isMarkdown && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex items-center p-0.5 bg-slate-100 rounded-md border flex-shrink-0"
                >
                  <span className="text-xs text-slate-500 px-2">
                    {searchMatches.length > 0
                      ? `${currentMatchIndex + 1}/${searchMatches.length}`
                      : "0/0"}
                  </span>
                  <button
                    onClick={handlePrevMatch}
                    className="p-1.5 text-slate-500 hover:text-green-600 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiChevronUp} />
                  </button>
                  <button
                    onClick={handleNextMatch}
                    className="p-1.5 text-slate-500 hover:text-green-600 disabled:opacity-50"
                  >
                    <SafeIcon icon={FiChevronDown} />
                  </button>
                  <button
                    onClick={clearSearch}
                    className="p-1.5 text-slate-400 hover:text-slate-600"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* --- START: กลุ่มปุ่ม Action ที่ออกแบบใหม่ให้สวยงามและสอดคล้องกัน --- */}
            <div className="flex items-center p-0.5 bg-slate-100 rounded-md border relative group">
              {/* MD Button */}
              <button
                onClick={toggleMarkdown}
                className={`p-1 rounded-md ${
                  // เพิ่ม px-2 เพื่อให้ text ไม่ชิดขอบ
                  isMarkdown ? "bg-green-100" : "hover:bg-slate-200"
                }`}
                title="Markdown View"
              >
                <span
                  className={`font-mono  text-xs ${
                    isMarkdown ? "text-green-700" : "text-slate-600"
                  }`}
                >
                  MD
                </span>
              </button>

              {/* Copy Button */}
              <button
                onClick={handleCopy}
                className={`p-1 rounded-md ${
                  isCopied ? "bg-green-100" : "hover:bg-slate-200"
                }`}
                title="Copy"
              >
                <SafeIcon
                  icon={isCopied ? FiCheck : FiCopy}
                  className={`w-4 h-4 ${
                    // ปรับขนาดไอคอนเป็น w-4 h-4
                    isCopied ? "text-green-600" : "text-slate-600"
                  }`}
                />
              </button>
            </div>
            {/* --- END: กลุ่มปุ่ม Action --- */}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50 border-b border-r border-gray-200">
        {showSummaryList ? (
          <SummaryList
            summaries={summaries}
            onDeleteSummary={onDeleteSummary}
            meetingDetails={meetingDetails}
            onSendEmail={onSendEmail}
            onUpdateSummaryTitle={handleUpdateSummaryTitle}
            onUpdateSummaryContent={handleUpdateSummaryContent}
          />
        ) : isMarkdown ? (
          <div
            ref={transcriptRef}
            className="prose max-w-none text-gray-800 leading-relaxed"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
              rehypePlugins={[rehypeRaw]}
            >
              {highlightedText}
            </ReactMarkdown>
          </div>
        ) : (
          <div
            ref={transcriptRef}
            className="prose max-w-none text-gray-800 leading-relaxed whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: highlightedText }}
          />
        )}
      </div>
      <div className="flex-shrink-0">
        <CollapsibleSection
          icon={FiLayers}
          title="รายการอ้างอิง"
          count={safeReferencedIds.length}
          onAdd={() => setIsReferenceModalOpen(true)}
        >
          {safeReferencedIds.length > 0 ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-4 custom-scrollbar-thin">
              <DndContext
                onDragEnd={handleReferenceDragEnd}
                sensors={sensors}
                collisionDetection={closestCenter}
              >
                <SortableContext
                  items={safeReferencedIds}
                  strategy={horizontalListSortingStrategy}
                >
                  {safeReferencedIds.map((id) => (
                    <SortableReferenceTag
                      key={id}
                      id={id}
                      title={meetingsMap.get(id)?.title || "..."}
                      onRemove={() => handleRemoveReference(id)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          ) : (
            <p className="text-xs text-center text-slate-500 py-2">
              ยังไม่มีรายการอ้างอิง
            </p>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          icon={FiPaperclip}
          title="ไฟล์แนบ"
          count={safeAttachments.length}
          onAdd={() => setIsAttachmentModalOpen(true)}
        >
          {safeAttachments.length > 0 ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-4 custom-scrollbar-thin">
              {safeAttachments.map((att) => (
                <AttachmentTag
                  key={att.id}
                  attachment={att}
                  onRemove={() => handleRemoveAttachment(att.id)}
                  onToggleUse={() => handleToggleAttachmentUse(att.id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-center text-slate-500 py-2">
              ยังไม่มีไฟล์แนบ
            </p>
          )}
        </CollapsibleSection>
      </div>
      <ReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={() => setIsReferenceModalOpen(false)}
        allMeetings={allMeetings || []} // *** FIX: ป้องกัน allMeetings เป็น null
        activeMeetingId={activeMeetingId} // *** FIX: ใช้ activeMeetingId ที่ส่งมา
        currentReferencedIds={safeReferencedIds}
        onSave={onUpdateReferences}
      />
      <MeetingDetailsForm
        isOpen={isDetailsFormOpen}
        onClose={() => setIsDetailsFormOpen(false)}
        onSave={handleSaveMeetingDetails}
        initialData={meetingDetails}
        onAIAssist={onAIAssist}
      />
      <AttachmentModal
        isOpen={isAttachmentModalOpen}
        onClose={() => setIsAttachmentModalOpen(false)}
        currentAttachments={safeAttachments}
        onSave={onUpdateAttachments}
      />
    </motion.div>
  );
};

export default TranscriptPanel;
